import { Accordion, ActionIcon, Badge, Card, Group, List, Stack, Text, Title, Tooltip, useMantineTheme } from "@mantine/core";
import { GridColDef } from "@mui/x-data-grid";
import { InfoIcon } from "@phosphor-icons/react";
import { IconEdit, IconEye, IconEyeOff, IconTrash } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { SubmitHandler } from "react-hook-form";

import { DataGridBase } from "@/components/DataGridBase";
import { useSelectedCompany } from "@/features/dashboard/configuracoes/_context/SelectedCompanyContext";
import { PopupForm, PopupSchema } from "@/features/dashboard/configuracoes/popups/_components/PopupForm";
import { Popup as DataPopup, useDeletePopupMutation, useGetPopupsByCompanyQuery, useSavePopupMutation } from "@/store";

export const Route = createFileRoute("/_private/dashboard/_settings/configuracoes/popups/")({
  component: RouteComponent
});

function RouteComponent() {
  const { colors } = useMantineTheme();
  const { companyId } = useSelectedCompany();
  const { data, isFetching } = useGetPopupsByCompanyQuery({ id: Number(companyId) }, { skip: !companyId });
  const [deletePopup] = useDeletePopupMutation();
  const [savePopup] = useSavePopupMutation();

  const navigate = useNavigate();

  const handleDeletePopup = async (id: number) => {
    await deletePopup({ id });
  };

  const onSubmit: SubmitHandler<PopupSchema> = async (values) => {
    const {
      title,
      active,
      content,
      dateRange: [start, end],
      label,
      image
    } = values;

    const startDate = dayjs(start).toDate();
    const endDate = end ? dayjs(end).toDate() : startDate;

    await savePopup({
      id: null,
      title,
      label,
      content,
      imageUrl: null,
      start_at: startDate,
      end_at: endDate,
      active,
      company: { id: Number(companyId) },
      $image: image
    });
  };

  const columns: GridColDef<DataPopup>[] = [
    {
      field: "id",
      headerName: "Pop-up",
      headerAlign: "left",
      display: "flex",
      flex: 1,
      type: "custom",
      minWidth: 175,
      renderCell: (params) => {
        const isActive = params.row.active;

        return (
          <div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Text>{params.value}</Text>
              <Badge size="xs" ml={8} color={isActive ? "green" : "gray"} leftSection={isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}>
                {isActive ? "Ativo" : "Inativo"}
              </Badge>
            </div>

            <Text>{params.row.label}</Text>
          </div>
        );
      }
    },
    { field: "start_at", headerName: "Início", flex: 1, type: "string", minWidth: 125, valueFormatter: (_, row) => (row.start_at ? dayjs(row.start_at).format("DD/MM/YYYY") : "") },
    { field: "end_at", headerName: "Término", flex: 1, type: "string", minWidth: 125, valueFormatter: (_, row) => (row.end_at ? dayjs(row.end_at).format("DD/MM/YYYY") : "") },
    { field: "views_count", headerName: "Visualizações", flex: 1, type: "number", minWidth: 115 },
    { field: "users_count", headerName: "Únicos", flex: 1, type: "number", minWidth: 100 },
    {
      field: "actions",
      headerName: "Ações",
      type: "actions",
      renderCell: (params) => (
        <ActionIcon.Group>
          <Tooltip label="Editar pop-up">
            <ActionIcon color="yellow" radius="sm" onClick={() => navigate({ to: "/dashboard/configuracoes/popups/$id/modal", params: { id: params.row.id } })}>
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Deletar pop-up">
            <ActionIcon color="red" radius="sm" onClick={() => handleDeletePopup(params.row.id)}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </ActionIcon.Group>
      )
    }
  ];

  return (
    <Stack gap="xl">
      <Group justify="space-between" align="center">
        <Title order={2}>Gerenciamento de Pop-ups</Title>
      </Group>

      {/* Formulário para adicionar/editar popup */}
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <PopupForm onSubmit={onSubmit} />
      </Card>

      {/* Tabela de popups */}
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <Stack gap="md">
          <Title order={4}>Pop-ups Cadastrados</Title>

          <DataGridBase rows={data || []} columns={columns} loading={isFetching} />
        </Stack>
      </Card>

      <Accordion>
        <Accordion.Item value="default">
          <Accordion.Control icon={<InfoIcon weight="fill" color={colors.blue[5]} />}>Sobre o Gerenciamento de Pop-ups</Accordion.Control>

          <Accordion.Panel>
            <List type="ordered" classNames={{ root: "list-decimal pl-5", item: "pl-1" }}>
              <List.Item>Pop-ups ajudam a comunicar informações importantes aos usuários</List.Item>
              <List.Item>Apenas pop-ups ativos são exibidos no sistema</List.Item>
              <List.Item>Use prioridades para controlar a ordem de exibição</List.Item>
              <List.Item>Defina datas para pop-ups temporários ou promocionais</List.Item>
              <List.Item>Configure a posição e duração conforme a importância da mensagem</List.Item>
              <List.Item>Pop-ups de alta prioridade (4-5) devem ser usados apenas para avisos críticos</List.Item>
            </List>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}
